import React from 'react';
import {useTranslation} from 'react-i18next';
import {ProjectPreviewState} from '../Context/Projects/ProjectsContext';
import {DesignerSettings} from '@taopix/taopix-core/dist/types/types';
import {Common} from '@taopix/taopix-core';
import {
  Dialog,
  DialogHeader,
  DialogContent,
  LoadingLosenge,
  ThemeName,
  Theme,
  DialogFooter,
  Button,
} from '@taopix/taopix-design-system';
import PreviewFactory from './PreviewFactory';
import {CollectionLayout} from '../Product/LayoutSelector';

export type PreviewDialogProps = ProjectPreviewState &
  Pick<DesignerSettings, 'productTheming'> & {
    onCancel: (event: React.MouseEvent) => void;
    onConfirm: (event: React.MouseEvent) => void;
    isSmallScreen: boolean;
  };

export const getThumbnail = (product: CollectionLayout, image: keyof CollectionLayout) => {
  if (!product.hasOwnProperty('collection')) {
    return 'https://placehold.co/100x100';
  }
  const thumb = `${window.config.baseURL}${product.collection.thumbnailPath}/products/${product.collection.collectionCode}/${product.collection.thumbpath}/${product[image]}`;
  if ('' === product[image]) {
    return 'https://placehold.co/100x100';
  }
  return thumb;
};

const PreviewDialog = (props: PreviewDialogProps) => {
  const {t} = useTranslation();

  const showThumbnail =
    !props.productTheming.livePreviewsEnabled ||
    (props.productTheming.livePreviewsEnabled && 0 === props.previews.length);

  return (
    <Dialog
      id={'themePreview-previewDialog'}
      open={props.open}
      width={1000}
      title={Common.getCurrentLocaleString(props.layout.name, true)}
    >
      <DialogContent>
        <div className="flex lg:space-x-lg sm:flex-col sm:space-y-lg sm:overflow-auto">
          <div className="lg:w-[600px] h-[500px] sm:w-full relative">
            {showThumbnail && (
              <img
                src={getThumbnail(props.layout, 'thumbnailImage')}
                alt={props.layout.name}
                className="object-cover"
                width={450}
              />
            )}

            {props.productTheming.livePreviewsEnabled && 0 === props.previews.length && (
              <LoadingLosenge
                label={t('toolpalette:str_TitleLoadingPreview')}
                key={'themes-loading-overlay'}
                theme={ThemeName.Container}
                className={'absolute bottom-0 w-full flex justify-center'}
              />
            )}
            {props.productTheming.livePreviewsEnabled && 0 !== props.previews.length && (
              <Theme
                name={ThemeName.Container}
                className="flex items-center justify-center p-sm w-full h-full"
              >
                <PreviewFactory
                  isSmallScreen={props.isSmallScreen}
                  width={props.dimensions.width}
                  height={props.dimensions.height}
                  startPage={1}
                  previews={props.previews}
                  classNames="items-center flex flex-row overflow-auto w-full h-full"
                  mode={props.productTheming.livePreviewMode}
                />
              </Theme>
            )}
          </div>
          <div className="flex-1">
            <p className="lg:text-[16px]">
              {Common.getCurrentLocaleString(props.layout.description, true)}
            </p>
          </div>
        </div>
      </DialogContent>
      <DialogFooter isSmallScreen={false}>
        <Button
          label={t('*:str_ButtonClose')}
          onClick={event => props.onCancel(event)}
          buttonStyle="negative"
          disabled={
            (props.productTheming.livePreviewsEnabled && 0 === props.previews.length) || false
          }
        />
        <Button
          label={t('toolpalette:str_ButtonSelectTheme')}
          buttonStyle="primary"
          onClick={event => props.onConfirm(event)}
          disabled={
            (props.productTheming.livePreviewsEnabled && 0 === props.previews.length) || false
          }
        />
      </DialogFooter>
    </Dialog>
  );
};

export default PreviewDialog;
